import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { setAdminToken } from '@/components/feature/AdminGuard';

const EDGE_FN_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

/* ══════════════════════════════════════════
   Pupil 组件 - 独立瞳孔（用于橙色/黄色怪物）
══════════════════════════════════════════ */
interface PupilProps {
  size?: number;
  maxDistance?: number;
  pupilColor?: string;
  forceLookX?: number;
  forceLookY?: number;
}

const Pupil = ({
  size = 12,
  maxDistance = 5,
  pupilColor = '#2D2D2D',
  forceLookX,
  forceLookY,
}: PupilProps) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const pupilRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getPos = () => {
    if (!pupilRef.current) return { x: 0, y: 0 };
    if (forceLookX !== undefined && forceLookY !== undefined) return { x: forceLookX, y: forceLookY };
    const rect = pupilRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mousePos.x - cx;
    const dy = mousePos.y - cy;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const angle = Math.atan2(dy, dx);
    return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
  };

  const pos = getPos();

  return (
    <div
      ref={pupilRef}
      className="rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: pupilColor,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        transition: 'transform 0.1s ease-out',
      }}
    />
  );
};

/* ══════════════════════════════════════════
   EyeBall 组件 - 带白色眼白的眼球（用于紫色/黑色怪物）
══════════════════════════════════════════ */
interface EyeBallProps {
  size?: number;
  pupilSize?: number;
  maxDistance?: number;
  eyeColor?: string;
  pupilColor?: string;
  isBlinking?: boolean;
  forceLookX?: number;
  forceLookY?: number;
}

const EyeBall = ({
  size = 48,
  pupilSize = 16,
  maxDistance = 10,
  eyeColor = 'white',
  pupilColor = '#2D2D2D',
  isBlinking = false,
  forceLookX,
  forceLookY,
}: EyeBallProps) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const eyeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getPos = () => {
    if (!eyeRef.current) return { x: 0, y: 0 };
    if (forceLookX !== undefined && forceLookY !== undefined) return { x: forceLookX, y: forceLookY };
    const rect = eyeRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mousePos.x - cx;
    const dy = mousePos.y - cy;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const angle = Math.atan2(dy, dx);
    return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
  };

  const pos = getPos();

  return (
    <div
      ref={eyeRef}
      className="rounded-full flex items-center justify-center"
      style={{
        width: size,
        height: isBlinking ? 2 : size,
        backgroundColor: eyeColor,
        overflow: 'hidden',
        transition: 'height 0.15s ease, border-radius 0.15s ease',
        borderRadius: isBlinking ? 2 : '50%',
      }}
    >
      {!isBlinking && (
        <div
          className="rounded-full"
          style={{
            width: pupilSize,
            height: pupilSize,
            backgroundColor: pupilColor,
            transform: `translate(${pos.x}px, ${pos.y}px)`,
            transition: 'transform 0.1s ease-out',
          }}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════
   登录页主体
══════════════════════════════════════════ */
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isExpired = (location.state as { expired?: boolean } | null)?.expired === true;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 怪物动画状态
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
  const [isBlackBlinking, setIsBlackBlinking] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking, setIsPurplePeeking] = useState(false);

  const purpleRef = useRef<HTMLDivElement>(null);
  const blackRef = useRef<HTMLDivElement>(null);
  const yellowRef = useRef<HTMLDivElement>(null);
  const orangeRef = useRef<HTMLDivElement>(null);

  // 鼠标追踪
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 紫色怪物随机眨眼
  useEffect(() => {
    const scheduleBlink = () => {
      const t = setTimeout(() => {
        setIsPurpleBlinking(true);
        setTimeout(() => {
          setIsPurpleBlinking(false);
          scheduleBlink();
        }, 150);
      }, Math.random() * 4000 + 3000);
      return t;
    };
    const t = scheduleBlink();
    return () => clearTimeout(t);
  }, []);

  // 黑色怪物随机眨眼
  useEffect(() => {
    const scheduleBlink = () => {
      const t = setTimeout(() => {
        setIsBlackBlinking(true);
        setTimeout(() => {
          setIsBlackBlinking(false);
          scheduleBlink();
        }, 150);
      }, Math.random() * 4000 + 3000);
      return t;
    };
    const t = scheduleBlink();
    return () => clearTimeout(t);
  }, []);

  // 输入时互相对视
  useEffect(() => {
    if (isTyping) {
      setIsLookingAtEachOther(true);
      const t = setTimeout(() => setIsLookingAtEachOther(false), 800);
      return () => clearTimeout(t);
    } else {
      setIsLookingAtEachOther(false);
    }
  }, [isTyping]);

  // 显示密码时紫色怪物偷看
  useEffect(() => {
    if (password.length > 0 && showPassword) {
      const schedulePeek = () => {
        const t = setTimeout(() => {
          setIsPurplePeeking(true);
          setTimeout(() => setIsPurplePeeking(false), 800);
        }, Math.random() * 3000 + 2000);
        return t;
      };
      const t = schedulePeek();
      return () => clearTimeout(t);
    } else {
      setIsPurplePeeking(false);
    }
  }, [password, showPassword, isPurplePeeking]);

  // 计算怪物身体倾斜和眼睛偏移
  const calculatePosition = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 3;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const faceX = Math.max(-15, Math.min(15, dx / 20));
    const faceY = Math.max(-10, Math.min(10, dy / 30));
    const bodySkew = Math.max(-6, Math.min(6, -dx / 120));
    return { faceX, faceY, bodySkew };
  }, [mouseX, mouseY]);

  const purplePos = calculatePosition(purpleRef);
  const blackPos = calculatePosition(blackRef);
  const yellowPos = calculatePosition(yellowRef);
  const orangePos = calculatePosition(orangeRef);

  // 是否遮眼（输入密码且未显示）
  const isCovering = password.length > 0 && !showPassword;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('请输入用户名和密码');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_login', username: username.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error ?? '用户名或密码错误');
      } else {
        setAdminToken(data.token);
        navigate('/admin', { replace: true });
      }
    } catch {
      setError('网络错误，请稍后重试');
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2"
      style={{ fontFamily: "'Inter','PingFang SC',sans-serif" }}
    >
      {/* ══ 左侧深色动画区 ══ */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 select-none overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1c1c1e 0%, #2a2a2e 50%, #1a1a1c 100%)',
        }}
      >
        {/* 网格背景 */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />
        <div
          className="absolute top-1/4 right-1/4 size-64 rounded-full blur-3xl"
          style={{ background: 'rgba(255,255,255,0.04)' }}
        />
        <div
          className="absolute bottom-1/4 left-1/4 size-96 rounded-full blur-3xl"
          style={{ background: 'rgba(255,255,255,0.03)' }}
        />

        {/* Logo */}
        <div className="relative z-20 flex items-center gap-2 text-lg font-semibold text-white">
          <div
            className="size-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)' }}
          >
            <i className="ri-store-2-line text-white text-sm" />
          </div>
          <span>商城后台</span>
        </div>

        {/* 怪物舞台 */}
        <div className="relative z-20 flex items-end justify-center" style={{ height: 500 }}>
          <div className="relative" style={{ width: 550, height: 400 }}>

            {/* 紫色高矩形 - 最后层 */}
            <div
              ref={purpleRef}
              className="absolute bottom-0 transition-all duration-700 ease-in-out"
              style={{
                left: 70,
                width: 180,
                height: isCovering ? 440 : 400,
                backgroundColor: '#6C3FF5',
                borderRadius: '10px 10px 0 0',
                zIndex: 1,
                transform: showPassword && password.length > 0
                  ? 'skewX(0deg)'
                  : isCovering
                    ? `skewX(${(purplePos.bodySkew || 0) - 12}deg) translateX(40px)`
                    : `skewX(${purplePos.bodySkew || 0}deg)`,
                transformOrigin: 'bottom center',
              }}
            >
              <div
                className="absolute flex gap-8 transition-all duration-700 ease-in-out"
                style={{
                  left: showPassword && password.length > 0 ? 20 : isLookingAtEachOther ? 55 : 45 + purplePos.faceX,
                  top: showPassword && password.length > 0 ? 35 : isLookingAtEachOther ? 65 : 40 + purplePos.faceY,
                }}
              >
                <EyeBall
                  size={18} pupilSize={7} maxDistance={5}
                  eyeColor="white" pupilColor="#2D2D2D"
                  isBlinking={isPurpleBlinking}
                  forceLookX={showPassword && password.length > 0 ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                  forceLookY={showPassword && password.length > 0 ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                />
                <EyeBall
                  size={18} pupilSize={7} maxDistance={5}
                  eyeColor="white" pupilColor="#2D2D2D"
                  isBlinking={isPurpleBlinking}
                  forceLookX={showPassword && password.length > 0 ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
                  forceLookY={showPassword && password.length > 0 ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
                />
              </div>
            </div>

            {/* 黑色矩形 - 中层 */}
            <div
              ref={blackRef}
              className="absolute bottom-0 transition-all duration-700 ease-in-out"
              style={{
                left: 240,
                width: 120,
                height: 310,
                backgroundColor: '#2D2D2D',
                borderRadius: '8px 8px 0 0',
                zIndex: 2,
                transform: showPassword && password.length > 0
                  ? 'skewX(0deg)'
                  : isLookingAtEachOther
                    ? `skewX(${(blackPos.bodySkew || 0) * 1.5 + 10}deg) translateX(20px)`
                    : isCovering
                      ? `skewX(${(blackPos.bodySkew || 0) * 1.5}deg)`
                      : `skewX(${blackPos.bodySkew || 0}deg)`,
                transformOrigin: 'bottom center',
              }}
            >
              <div
                className="absolute flex gap-6 transition-all duration-700 ease-in-out"
                style={{
                  left: showPassword && password.length > 0 ? 10 : isLookingAtEachOther ? 32 : 26 + blackPos.faceX,
                  top: showPassword && password.length > 0 ? 28 : isLookingAtEachOther ? 12 : 32 + blackPos.faceY,
                }}
              >
                <EyeBall
                  size={16} pupilSize={6} maxDistance={4}
                  eyeColor="white" pupilColor="#2D2D2D"
                  isBlinking={isBlackBlinking}
                  forceLookX={showPassword && password.length > 0 ? -4 : isLookingAtEachOther ? 0 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : isLookingAtEachOther ? -4 : undefined}
                />
                <EyeBall
                  size={16} pupilSize={6} maxDistance={4}
                  eyeColor="white" pupilColor="#2D2D2D"
                  isBlinking={isBlackBlinking}
                  forceLookX={showPassword && password.length > 0 ? -4 : isLookingAtEachOther ? 0 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : isLookingAtEachOther ? -4 : undefined}
                />
              </div>
            </div>

            {/* 橙色半圆 - 前左 */}
            <div
              ref={orangeRef}
              className="absolute bottom-0 transition-all duration-700 ease-in-out"
              style={{
                left: 0,
                width: 240,
                height: 200,
                backgroundColor: '#FF9B6B',
                borderRadius: '120px 120px 0 0',
                zIndex: 3,
                transform: showPassword && password.length > 0
                  ? 'skewX(0deg)'
                  : `skewX(${orangePos.bodySkew || 0}deg)`,
                transformOrigin: 'bottom center',
              }}
            >
              <div
                className="absolute flex gap-8 transition-all duration-200 ease-out"
                style={{
                  left: showPassword && password.length > 0 ? 50 : 82 + (orangePos.faceX || 0),
                  top: showPassword && password.length > 0 ? 85 : 90 + (orangePos.faceY || 0),
                }}
              >
                <Pupil
                  size={12} maxDistance={5} pupilColor="#2D2D2D"
                  forceLookX={showPassword && password.length > 0 ? -5 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : undefined}
                />
                <Pupil
                  size={12} maxDistance={5} pupilColor="#2D2D2D"
                  forceLookX={showPassword && password.length > 0 ? -5 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : undefined}
                />
              </div>
            </div>

            {/* 黄色胶囊 - 最前层右侧 */}
            <div
              ref={yellowRef}
              className="absolute bottom-0 transition-all duration-700 ease-in-out"
              style={{
                left: 310,
                width: 140,
                height: 230,
                backgroundColor: '#E8D754',
                borderRadius: '70px 70px 0 0',
                zIndex: 4,
                transform: showPassword && password.length > 0
                  ? 'skewX(0deg)'
                  : `skewX(${yellowPos.bodySkew || 0}deg)`,
                transformOrigin: 'bottom center',
              }}
            >
              <div
                className="absolute flex gap-6 transition-all duration-200 ease-out"
                style={{
                  left: showPassword && password.length > 0 ? 20 : 52 + (yellowPos.faceX || 0),
                  top: showPassword && password.length > 0 ? 35 : 40 + (yellowPos.faceY || 0),
                }}
              >
                <Pupil
                  size={12} maxDistance={5} pupilColor="#2D2D2D"
                  forceLookX={showPassword && password.length > 0 ? -5 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : undefined}
                />
                <Pupil
                  size={12} maxDistance={5} pupilColor="#2D2D2D"
                  forceLookX={showPassword && password.length > 0 ? -5 : undefined}
                  forceLookY={showPassword && password.length > 0 ? -4 : undefined}
                />
              </div>
              {/* 嘴巴 */}
              <div
                className="absolute rounded-full transition-all duration-200 ease-out"
                style={{
                  width: 80,
                  height: 4,
                  backgroundColor: '#2D2D2D',
                  left: showPassword && password.length > 0 ? 10 : 40 + (yellowPos.faceX || 0),
                  top: showPassword && password.length > 0 ? 88 : 88 + (yellowPos.faceY || 0),
                }}
              />
            </div>

          </div>
        </div>

        {/* 底部占位 */}
        <div className="relative z-20" />
      </div>

      {/* ══ 右侧白色登录表单 ══ */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-[420px]">

          {/* 移动端 Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 text-lg font-semibold mb-12">
            <div className="size-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <i className="ri-store-2-line text-gray-700 text-sm" />
            </div>
            <span>商城后台</span>
          </div>

          {/* 标题 */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold tracking-tight mb-2 text-gray-900">欢迎回来！</h1>
            <p className="text-gray-400 text-sm">请输入管理员账号登录</p>
          </div>

          {/* 会话过期提示 */}
          {isExpired && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2.5 rounded-md mb-6">
              <i className="ri-time-line flex-shrink-0" />
              登录已超过24小时，请重新登录
            </div>
          )}

          <form className="space-y-5" onSubmit={handleLogin}>
            {/* 用户名 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="username">
                用户名
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
                onFocus={() => setIsTyping(true)}
                onBlur={() => setIsTyping(false)}
                placeholder="请输入用户名"
                autoComplete="off"
                className="flex w-full rounded-md border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 h-12 bg-white"
              />
            </div>

            {/* 密码 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="password">
                密码
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  className="flex w-full rounded-md border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 h-12 pr-10 bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-base`} />
                </button>
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 px-3 py-2.5 rounded-md">
                <i className="ri-error-warning-line flex-shrink-0" />
                {error}
              </div>
            )}

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-8 w-full h-12 text-base font-medium text-white cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
              style={{ background: '#1a1a1a' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#333'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1a1a1a'; }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  登录中...
                </span>
              ) : '登 录'}
            </button>
          </form>

          <div className="text-center text-sm text-gray-400 mt-8">
            仅限授权管理员访问
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
